/* global localStorage, sessionStorage */

/**
 * Safe Browser Storage Utilities
 *
 * Provides safe access to localStorage and sessionStorage with automatic
 * error handling for environments where storage is unavailable:
 * - Private/incognito browsing
 * - Storage quota exceeded
 * - Third-party cookie blocking
 * - SSR environments
 *
 * All functions fail silently and return sensible defaults.
 *
 * @module utils/storage
 */

// =============================================================================
// localStorage
// =============================================================================

/**
 * Safely get item from localStorage
 *
 * @param {string} key - Storage key
 * @param {*} fallback - Value to return if key doesn't exist or storage fails
 * @returns {string|*} Stored value or fallback
 *
 * @example
 * const theme = getItem('theme', 'light')
 */
export function getItem (key, fallback = null) {
  try {
    const value = localStorage.getItem(key)
    return value !== null ? value : fallback
  } catch {
    return fallback
  }
}

/**
 * Safely set item in localStorage
 *
 * @param {string} key - Storage key
 * @param {string} value - Value to store (will be converted to string)
 * @returns {boolean} True if successful, false otherwise
 *
 * @example
 * setItem('theme', 'dark')
 */
export function setItem (key, value) {
  try {
    localStorage.setItem(key, String(value))
    return true
  } catch {
    return false
  }
}

/**
 * Safely remove item from localStorage
 *
 * @param {string} key - Storage key
 * @returns {boolean} True if successful, false otherwise
 *
 * @example
 * removeItem('theme')
 */
export function removeItem (key) {
  try {
    localStorage.removeItem(key)
    return true
  } catch {
    return false
  }
}

// =============================================================================
// sessionStorage
// =============================================================================

/**
 * Safely get item from sessionStorage
 *
 * @param {string} key - Storage key
 * @param {*} fallback - Value to return if key doesn't exist or storage fails
 * @returns {string|*} Stored value or fallback
 *
 * @example
 * const minimized = getSessionItem('banner-minimized', 'false')
 */
export function getSessionItem (key, fallback = null) {
  try {
    const value = sessionStorage.getItem(key)
    return value !== null ? value : fallback
  } catch {
    return fallback
  }
}

/**
 * Safely set item in sessionStorage
 *
 * @param {string} key - Storage key
 * @param {string} value - Value to store (will be converted to string)
 * @returns {boolean} True if successful, false otherwise
 *
 * @example
 * setSessionItem('banner-minimized', 'true')
 */
export function setSessionItem (key, value) {
  try {
    sessionStorage.setItem(key, String(value))
    return true
  } catch {
    return false
  }
}

/**
 * Safely remove item from sessionStorage
 *
 * @param {string} key - Storage key
 * @returns {boolean} True if successful, false otherwise
 *
 * @example
 * removeSessionItem('banner-minimized')
 */
export function removeSessionItem (key) {
  try {
    sessionStorage.removeItem(key)
    return true
  } catch {
    return false
  }
}
